import * as THREE from 'three';
import {
  createAmbientOcclusionTexture,
  createCanvasWeaveTexture,
  createHullSideTexture,
  createNormalTexture,
  createRoughnessTexture,
  createWoodGrainTexture,
  packOrmTexture,
} from './proceduralTextures';

/**
 * Procedural PBR material set for the procedural ship hull.
 *
 * The grain/weave maps are shared singletons (the colour comes from the
 * per-vessel palette multiplied against them), which keeps the texture budget
 * low while still giving every plank and sail a hand-made look. Materials are
 * `MeshStandardMaterial`, so they automatically sample `scene.environment` for
 * sky reflections; `envMapIntensity` is tuned per surface.
 */

export interface ShipMaterialColors {
  hull: number;
  deck: number;
  trim: number;
  sail: number;
  mast: number;
  helm: number;
  sailStation: number;
  railing: number;
  window: number;
  rope: number;
  plankDark: number;
}

export interface ShipMaterialSet {
  hull: THREE.MeshStandardMaterial;
  deck: THREE.MeshStandardMaterial;
  trim: THREE.MeshStandardMaterial;
  mast: THREE.MeshStandardMaterial;
  sail: THREE.MeshStandardMaterial;
  railing: THREE.MeshStandardMaterial;
  window: THREE.MeshStandardMaterial;
  rope: THREE.MeshStandardMaterial;
  plankDark: THREE.MeshStandardMaterial;
  dispose(): void;
}

interface SharedMaps {
  wood: THREE.CanvasTexture;
  /** Packed AO (R) + roughness (G) + metalness (B) map shared by all wood. */
  orm: THREE.CanvasTexture;
  woodNormal: THREE.CanvasTexture;
  /** Dedicated weathered hull-side detail map (shared, never per-ship). */
  hullSide: THREE.CanvasTexture;
  canvas: THREE.CanvasTexture;
  canvasNormal: THREE.CanvasTexture;
}

let sharedMaps: SharedMaps | null = null;

/** Lazily build (and cache) the texture maps shared by every ship. */
function getSharedMaps(): SharedMaps {
  if (!sharedMaps) {
    // Build + pack the ORM map once; the intermediate AO/roughness canvases
    // are only needed while packing and are disposed straight away.
    const ao = createAmbientOcclusionTexture(128, 1337);
    const roughness = createRoughnessTexture(128, 2024, 0.45, 0.95);
    sharedMaps = {
      wood: createWoodGrainTexture(256, 1337),
      orm: packOrmTexture(ao, roughness, 0, 128),
      woodNormal: createNormalTexture(128, 2024, 1.7),
      hullSide: createHullSideTexture(1337, 256),
      canvas: createCanvasWeaveTexture(128, 31337),
      canvasNormal: createNormalTexture(128, 31337, 0.8),
    };
    ao.dispose();
    roughness.dispose();
  }
  return sharedMaps;
}

interface WoodMaterialOptions {
  color: number;
  roughness: number;
  metalness: number;
  envMapIntensity: number;
  /** Enable the shared plank-seam AO map (ORM red channel). Default true. */
  ao?: boolean;
  /** Override the shared wood grain albedo (the hull uses its weathered map). */
  map?: THREE.Texture;
}

/**
 * Build a tinted wood material from the shared grain/ORM/normal maps.
 *
 * Roughness samples the ORM green channel and ambient occlusion the red
 * channel; metalness stays a scalar because wood is dielectric (the ORM blue
 * channel is reserved for the metallic realistic hero).
 */
function woodMaterial(maps: SharedMaps, options: WoodMaterialOptions): THREE.MeshStandardMaterial {
  const material = new THREE.MeshStandardMaterial({
    color: options.color,
    map: options.map ?? maps.wood,
    roughnessMap: maps.orm,
    normalMap: maps.woodNormal,
    roughness: options.roughness,
    metalness: options.metalness,
    envMapIntensity: options.envMapIntensity,
  });
  if (options.ao !== false) {
    material.aoMap = maps.orm;
    material.aoMapIntensity = 1;
  }
  return material;
}

export function createShipMaterials(
  colors: ShipMaterialColors,
  seed = 1337,
): ShipMaterialSet {
  // `seed` is retained in the signature so callers can request variation in the
  // future; the shared maps are intentionally identical across ships today.
  void seed;

  const maps = getSharedMaps();

  const materials: THREE.MeshStandardMaterial[] = [];

  const hull = woodMaterial(maps, {
    color: colors.hull,
    roughness: 0.72,
    metalness: 0.05,
    envMapIntensity: 0.35,
    ao: false, // the hull albedo already bakes its plank seams + weathering
    map: maps.hullSide,
  });
  const deck = woodMaterial(maps, {
    color: colors.deck,
    roughness: 0.82,
    metalness: 0.04,
    envMapIntensity: 0.18,
  });
  const trim = woodMaterial(maps, {
    color: colors.trim,
    roughness: 0.66,
    metalness: 0.06,
    envMapIntensity: 0.3,
  });
  const mast = woodMaterial(maps, {
    color: colors.mast,
    roughness: 0.55,
    metalness: 0.05,
    envMapIntensity: 0.3,
  });
  const railing = woodMaterial(maps, {
    color: colors.railing,
    roughness: 0.6,
    metalness: 0.05,
    envMapIntensity: 0.3,
  });
  const plankDark = woodMaterial(maps, {
    color: colors.plankDark,
    roughness: 0.85,
    metalness: 0.02,
    envMapIntensity: 0.15,
  });

  const sail = new THREE.MeshStandardMaterial({
    color: colors.sail,
    map: maps.canvas,
    normalMap: maps.canvasNormal,
    roughness: 0.92,
    metalness: 0,
    side: THREE.DoubleSide,
    transparent: true,
    opacity: 0.96,
    envMapIntensity: 0.55,
  });

  const window = new THREE.MeshStandardMaterial({
    color: colors.window,
    roughness: 0.18,
    metalness: 0.35,
    emissive: colors.window,
    emissiveIntensity: 0.15,
    envMapIntensity: 1.1,
  });

  const rope = new THREE.MeshStandardMaterial({
    color: colors.rope,
    roughness: 0.85,
    metalness: 0.02,
    envMapIntensity: 0.2,
  });

  materials.push(hull, deck, trim, mast, sail, railing, window, rope, plankDark);

  return {
    hull,
    deck,
    trim,
    mast,
    sail,
    railing,
    window,
    rope,
    plankDark,
    dispose(): void {
      for (const material of materials) material.dispose();
    },
  };
}

/** Dispose the shared procedural maps (called once at teardown). */
export function disposeSharedShipMaps(): void {
  if (!sharedMaps) return;
  sharedMaps.wood.dispose();
  sharedMaps.orm.dispose();
  sharedMaps.woodNormal.dispose();
  sharedMaps.hullSide.dispose();
  sharedMaps.canvas.dispose();
  sharedMaps.canvasNormal.dispose();
  sharedMaps = null;
}
